import { SentimentOption } from '@modules/feedback/components/FeedbackDetails/EditPredictionsPanel/SentimentOption';
import { getSentimentNameFromDisplayName } from '@modules/feedback/components/FeedbackDetails/utils';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import {
  getReasonAndTrackedKeywordPredictions,
  getSentimentPrediction,
  getTextSentimentColor,
} from '@modules/feedback/utils';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { getFeedbackSentimentColor } from '@modules/feedbackV2/common/utils';
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Theme,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { ManipulationType, SentimentName } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { Feedback, FeedbackLabelType } from 'v2/core/entities/feedback';

interface EditSentimentViewProps {
  sentiment: SentimentName;
  feedback: Feedback;
}

export const EditSentimentView = ({
  sentiment,
  feedback,
}: EditSentimentViewProps) => {
  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const theme: Theme = useTheme();

  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );

  const editedRecord = editedRecords.find(
    (editedRecord) => editedRecord.id === feedback.id,
  );

  const editedSentiment = getSentimentPrediction(editedRecord)?.prediction;

  const recordInEditModeSentiment =
    getSentimentPrediction(recordInEditMode)?.prediction;

  const existingSentiment = getSentimentNameFromDisplayName(
    editedSentiment ?? sentiment,
  );
  const [feedbackSentiment, setFeedbackSentiment] =
    useState<SentimentName>(existingSentiment);

  useEffect(() => {
    if (!recordInEditModeSentiment) {
      setFeedbackSentiment(existingSentiment);
    }
  }, [existingSentiment, recordInEditModeSentiment]);

  const onChange = (event: SelectChangeEvent) => {
    const updatedSentiment = getSentimentNameFromDisplayName(
      event.target.value,
    );
    setFeedbackSentiment(updatedSentiment);

    const nonSentimentPredictions =
      getReasonAndTrackedKeywordPredictions(recordInEditMode);

    dispatch({
      type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
      payload: {
        id: feedback.id,
        editPredictions:
          updatedSentiment === existingSentiment
            ? nonSentimentPredictions
            : [
                ...(nonSentimentPredictions ?? []),
                {
                  type: FeedbackLabelType.Sentiment,
                  manipulationType: ManipulationType.Add,
                  prediction: updatedSentiment,
                },
              ],
      },
    });
  };

  const getBorderColor = (theme: Theme) => {
    if (isFeedbackRedesignEnabled) {
      return getFeedbackSentimentColor({ theme, sentiment: feedbackSentiment });
    }

    return getTextSentimentColor(feedbackSentiment) ?? undefined;
  };

  return (
    <Stack spacing={1}>
      <FormControl
        sx={{
          m: isFeedbackRedesignEnabled ? 0 : 1,
          width: isFeedbackRedesignEnabled ? 140 : 210,
        }}
        size="small"
      >
        <Select
          value={feedbackSentiment}
          onChange={onChange}
          SelectDisplayProps={{
            style: {
              border: '2px solid',
              borderColor: getBorderColor(theme),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        >
          <MenuItem value={SentimentName.Positive}>
            <SentimentOption sentiment={SentimentName.Positive} />
          </MenuItem>
          <MenuItem value={SentimentName.Neutral}>
            <SentimentOption sentiment={SentimentName.Neutral} />
          </MenuItem>
          <MenuItem value={SentimentName.Negative}>
            <SentimentOption sentiment={SentimentName.Negative} />
          </MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );
};
